import {
  Alert,
  Button,
  Card,
  Col,
  DatePicker,
  Empty,
  Form,
  Row,
  Select,
  Space,
  Spin,
  Statistic,
  Table,
  Tabs,
  Typography,
  message,
} from 'antd';
import {
  DownloadOutlined,
  ReloadOutlined,
  SearchOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  getEffectStatisticsOverview,
  getEffectStatisticsQuery,
  getProductionStatisticsOverview,
  getProductionStatisticsQuery,
  getPublishFailureReasons,
  getPublishStatisticsOverview,
  getPublishStatisticsQuery,
  getStatisticsFilterOptions,
} from '../api';
import { formatShanghaiDateTime } from '../utils/datetime';
import './Statistics.css';

const { Title, Paragraph, Text } = Typography;
const { RangePicker } = DatePicker;

const TAB_OPTIONS = ['production', 'publish', 'effect'];
const SHANGHAI_TIMEZONE = 'Asia/Shanghai';
const DEFAULT_PAGE_SIZE = 10;
const EXPORT_PAGE_SIZE = 100;

const PRODUCTION_TIME_FIELD_OPTIONS = [
  { label: '创建时间', value: 'created_at' },
  { label: '入库时间', value: 'ingested_at' },
];
const PUBLISH_TIME_FIELD_OPTIONS = [
  { label: '发布时间', value: 'publish_time' },
  { label: '创建时间', value: 'created_at' },
];
const GRANULARITY_OPTIONS = [
  { label: '按天', value: 'day' },
  { label: '按周', value: 'week' },
  { label: '按月', value: 'month' },
];
const PRODUCTION_GROUP_OPTIONS = [
  { label: '日期', value: 'date' },
  { label: '内容类型', value: 'content_type' },
  { label: '内容来源', value: 'content_source' },
  { label: '生产器', value: 'producer' },
];
const PRODUCTION_TOP_OPTIONS = [
  { label: '内容类型', value: 'content_type' },
  { label: '内容来源', value: 'content_source' },
  { label: '生产器', value: 'producer' },
];
const PUBLISH_GROUP_OPTIONS = [
  { label: '日期', value: 'date' },
  { label: '平台', value: 'platform_code' },
  { label: '账号', value: 'account_id' },
  { label: '内容类型', value: 'template_type' },
  { label: '发布状态', value: 'status' },
  { label: '发布人', value: 'publisher_user_id' },
  { label: '任务 ID', value: 'task_id' },
];
const PUBLISH_TOP_OPTIONS = [
  { label: '平台', value: 'platform_code' },
  { label: '账号', value: 'account_id' },
  { label: '内容类型', value: 'template_type' },
  { label: '发布人', value: 'publisher_user_id' },
  { label: '任务 ID', value: 'task_id' },
];
const EFFECT_MODE_OPTIONS = [
  { label: '当前累计', value: 'current' },
  { label: '期间新增', value: 'delta' },
];
const EFFECT_CURRENT_TIME_FIELD_OPTIONS = [
  { label: '发布时间', value: 'publish_time' },
  { label: '最近同步时间', value: 'effect_last_synced_at' },
  { label: '创建时间', value: 'created_at' },
];
const EFFECT_DELTA_TIME_FIELD_OPTIONS = [
  { label: '采集日期', value: 'business_date' },
];
const EFFECT_GROUP_OPTIONS = [
  { label: '日期', value: 'date' },
  { label: '平台', value: 'platform_code' },
  { label: '账号', value: 'account_id' },
  { label: '内容类型', value: 'template_type' },
  { label: '任务 ID', value: 'task_id' },
  { label: '内容来源', value: 'content_source' },
  { label: '生产器', value: 'producer' },
  { label: '同步状态', value: 'effect_sync_status' },
];
const EFFECT_METRIC_OPTIONS = [
  { label: '播放量', value: 'view_count' },
  { label: '点赞量', value: 'like_count' },
  { label: '评论量', value: 'comment_count' },
  { label: '转发量', value: 'share_count' },
  { label: '收藏量', value: 'favorite_count' },
];

const PRODUCTION_DIMENSION_LABELS = {
  date: '日期',
  content_type: '内容类型',
  content_source: '内容来源',
  producer: '生产器',
};
const PUBLISH_DIMENSION_LABELS = {
  date: '日期',
  platform_code: '平台',
  account_id: '账号',
  template_type: '内容类型',
  status: '发布状态',
  publisher_user_id: '发布人',
  task_id: '任务 ID',
};
const EFFECT_DIMENSION_LABELS = {
  date: '日期',
  platform_code: '平台',
  account_id: '账号',
  template_type: '内容类型',
  task_id: '任务 ID',
  content_source: '内容来源',
  producer: '生产器',
  effect_sync_status: '同步状态',
};

const createPanelState = () => ({
  loading: false,
  loaded: false,
  error: '',
  overview: null,
  query: null,
  extra: null,
  options: null,
  optionsLoading: false,
  optionsAttempted: false,
  optionsError: '',
  exporting: false,
});

const buildLastDaysRange = (days = 7) => {
  const end = dayjs();
  const start = end.subtract(days - 1, 'day');
  return [start, end];
};

const createDefaultProductionQuery = () => ({
  timeField: 'created_at',
  range: buildLastDaysRange(7),
  contentTypes: [],
  contentSources: [],
  producers: [],
  groupBy: ['content_type'],
  granularity: 'day',
  topBy: 'producer',
  page: 1,
  pageSize: DEFAULT_PAGE_SIZE,
});

const createDefaultPublishQuery = () => ({
  timeField: 'publish_time',
  range: buildLastDaysRange(7),
  platformCodes: [],
  accountIds: [],
  statuses: [],
  templateTypes: [],
  publisherUserIds: [],
  taskIds: [],
  groupBy: ['account_id', 'platform_code'],
  granularity: 'day',
  topBy: 'account_id',
  page: 1,
  pageSize: DEFAULT_PAGE_SIZE,
});

const createDefaultEffectQuery = () => ({
  mode: 'current',
  timeField: 'publish_time',
  range: buildLastDaysRange(7),
  platformCodes: [],
  accountIds: [],
  templateTypes: [],
  taskIds: [],
  contentSources: [],
  producers: [],
  effectSyncStatuses: [],
  groupBy: ['platform_code', 'template_type'],
  granularity: 'day',
  topMetric: 'view_count',
  page: 1,
  pageSize: DEFAULT_PAGE_SIZE,
});

const parseTabFromSearch = (search) => {
  const tab = new URLSearchParams(search || '').get('tab');
  return TAB_OPTIONS.includes(tab) ? tab : 'production';
};

const ensureStringArray = (value) => {
  if (Array.isArray(value)) {
    return value
      .map((item) => String(item || '').trim())
      .filter(Boolean);
  }
  const text = String(value || '').trim();
  return text ? [text] : [];
};

const normalizeAllowedValue = (value, allowedValues, defaultValue) => {
  const text = String(value || '').trim();
  if (!text) return defaultValue;
  return allowedValues.includes(text) ? text : defaultValue;
};

const normalizeAllowedList = (value, allowedValues, defaultValue, maxItems) => {
  const items = ensureStringArray(value);
  const nextItems = [];
  items.forEach((item) => {
    if (!allowedValues.includes(item)) return;
    if (nextItems.includes(item)) return;
    if (nextItems.length >= maxItems) return;
    nextItems.push(item);
  });
  return nextItems.length > 0 ? nextItems : [...defaultValue];
};

const parseCsvParam = (searchParams, key) => {
  const raw = String(searchParams.get(key) || '').trim();
  if (!raw) return [];
  return raw.split(',').map((item) => item.trim()).filter(Boolean);
};

const parsePositiveIntParam = (searchParams, key, defaultValue, maximum = 100) => {
  const raw = Number(searchParams.get(key));
  if (!Number.isFinite(raw)) return defaultValue;
  const value = Math.floor(raw);
  if (value < 1) return defaultValue;
  return Math.min(value, maximum);
};

const parseDateRange = (searchParams, fromKey, toKey, fallbackRange) => {
  const fromRaw = String(searchParams.get(fromKey) || '').trim();
  const toRaw = String(searchParams.get(toKey) || '').trim();
  if (!fromRaw || !toRaw) return fallbackRange;
  const fromValue = dayjs(fromRaw);
  const toValue = dayjs(toRaw);
  if (!fromValue.isValid() || !toValue.isValid() || fromValue.isAfter(toValue)) {
    return fallbackRange;
  }
  return [fromValue, toValue];
};

const parseProductionQueryFromSearch = (search) => {
  const searchParams = new URLSearchParams(search || '');
  const defaults = createDefaultProductionQuery();
  return {
    timeField: normalizeAllowedValue(
      searchParams.get('prod_time_field'),
      PRODUCTION_TIME_FIELD_OPTIONS.map((item) => item.value),
      defaults.timeField
    ),
    range: parseDateRange(searchParams, 'prod_from', 'prod_to', defaults.range),
    contentTypes: parseCsvParam(searchParams, 'prod_content_type'),
    contentSources: parseCsvParam(searchParams, 'prod_content_source'),
    producers: parseCsvParam(searchParams, 'prod_producer'),
    groupBy: normalizeAllowedList(
      parseCsvParam(searchParams, 'prod_group_by'),
      PRODUCTION_GROUP_OPTIONS.map((item) => item.value),
      defaults.groupBy,
      2
    ),
    granularity: normalizeAllowedValue(
      searchParams.get('prod_granularity'),
      GRANULARITY_OPTIONS.map((item) => item.value),
      defaults.granularity
    ),
    topBy: normalizeAllowedValue(
      searchParams.get('prod_top_by'),
      PRODUCTION_TOP_OPTIONS.map((item) => item.value),
      defaults.topBy
    ),
    page: parsePositiveIntParam(searchParams, 'prod_page', defaults.page, 500),
    pageSize: parsePositiveIntParam(searchParams, 'prod_page_size', defaults.pageSize, 100),
  };
};

const parsePublishQueryFromSearch = (search) => {
  const searchParams = new URLSearchParams(search || '');
  const defaults = createDefaultPublishQuery();
  return {
    timeField: normalizeAllowedValue(
      searchParams.get('pub_time_field'),
      PUBLISH_TIME_FIELD_OPTIONS.map((item) => item.value),
      defaults.timeField
    ),
    range: parseDateRange(searchParams, 'pub_from', 'pub_to', defaults.range),
    platformCodes: parseCsvParam(searchParams, 'pub_platform_code'),
    accountIds: parseCsvParam(searchParams, 'pub_account_id'),
    statuses: parseCsvParam(searchParams, 'pub_status'),
    templateTypes: parseCsvParam(searchParams, 'pub_template_type'),
    publisherUserIds: parseCsvParam(searchParams, 'pub_publisher_user_id'),
    taskIds: parseCsvParam(searchParams, 'pub_task_id'),
    groupBy: normalizeAllowedList(
      parseCsvParam(searchParams, 'pub_group_by'),
      PUBLISH_GROUP_OPTIONS.map((item) => item.value),
      defaults.groupBy,
      3
    ),
    granularity: normalizeAllowedValue(
      searchParams.get('pub_granularity'),
      GRANULARITY_OPTIONS.map((item) => item.value),
      defaults.granularity
    ),
    topBy: normalizeAllowedValue(
      searchParams.get('pub_top_by'),
      PUBLISH_TOP_OPTIONS.map((item) => item.value),
      defaults.topBy
    ),
    page: parsePositiveIntParam(searchParams, 'pub_page', defaults.page, 500),
    pageSize: parsePositiveIntParam(searchParams, 'pub_page_size', defaults.pageSize, 100),
  };
};

const parseEffectQueryFromSearch = (search) => {
  const searchParams = new URLSearchParams(search || '');
  const defaults = createDefaultEffectQuery();
  const mode = normalizeAllowedValue(
    searchParams.get('eff_mode'),
    EFFECT_MODE_OPTIONS.map((item) => item.value),
    defaults.mode
  );
  const timeFieldOptions = mode === 'delta' ? EFFECT_DELTA_TIME_FIELD_OPTIONS : EFFECT_CURRENT_TIME_FIELD_OPTIONS;
  return {
    mode,
    timeField: normalizeAllowedValue(
      searchParams.get('eff_time_field'),
      timeFieldOptions.map((item) => item.value),
      mode === 'delta' ? 'business_date' : defaults.timeField
    ),
    range: parseDateRange(searchParams, 'eff_from', 'eff_to', defaults.range),
    platformCodes: parseCsvParam(searchParams, 'eff_platform_code'),
    accountIds: parseCsvParam(searchParams, 'eff_account_id'),
    templateTypes: parseCsvParam(searchParams, 'eff_template_type'),
    taskIds: parseCsvParam(searchParams, 'eff_task_id'),
    contentSources: parseCsvParam(searchParams, 'eff_content_source'),
    producers: parseCsvParam(searchParams, 'eff_producer'),
    effectSyncStatuses: parseCsvParam(searchParams, 'eff_sync_status'),
    groupBy: normalizeAllowedList(
      parseCsvParam(searchParams, 'eff_group_by'),
      EFFECT_GROUP_OPTIONS.map((item) => item.value),
      defaults.groupBy,
      3
    ),
    granularity: normalizeAllowedValue(
      searchParams.get('eff_granularity'),
      GRANULARITY_OPTIONS.map((item) => item.value),
      defaults.granularity
    ),
    topMetric: normalizeAllowedValue(
      searchParams.get('eff_top_metric'),
      EFFECT_METRIC_OPTIONS.map((item) => item.value),
      defaults.topMetric
    ),
    page: parsePositiveIntParam(searchParams, 'eff_page', defaults.page, 500),
    pageSize: parsePositiveIntParam(searchParams, 'eff_page_size', defaults.pageSize, 100),
  };
};

const normalizeRange = (range, fallback = buildLastDaysRange(7)) => {
  if (!Array.isArray(range) || range.length !== 2) return fallback;
  const [start, end] = range;
  if (!dayjs.isDayjs(start) || !dayjs.isDayjs(end)) return fallback;
  if (!start.isValid() || !end.isValid() || start.isAfter(end)) return fallback;
  return [start, end];
};

const formatDateOnly = (value) => {
  if (dayjs.isDayjs(value)) return value.format('YYYY-MM-DD');
  const parsed = dayjs(value);
  return parsed.isValid() ? parsed.format('YYYY-MM-DD') : '';
};

const formatPercent = (value) => `${(Number(value || 0) * 100).toFixed(1)}%`;

const buildProductionFormValues = (queryState) => ({
  timeField: queryState.timeField,
  range: normalizeRange(queryState.range),
  contentTypes: [...queryState.contentTypes],
  contentSources: [...queryState.contentSources],
  producers: [...queryState.producers],
  groupBy: [...queryState.groupBy],
  granularity: queryState.granularity,
  topBy: queryState.topBy,
});

const buildPublishFormValues = (queryState) => ({
  timeField: queryState.timeField,
  range: normalizeRange(queryState.range),
  platformCodes: [...queryState.platformCodes],
  accountIds: [...queryState.accountIds],
  statuses: [...queryState.statuses],
  templateTypes: [...queryState.templateTypes],
  publisherUserIds: [...queryState.publisherUserIds],
  taskIds: [...queryState.taskIds],
  groupBy: [...queryState.groupBy],
  granularity: queryState.granularity,
  topBy: queryState.topBy,
});

const buildEffectFormValues = (queryState) => ({
  mode: queryState.mode,
  timeField: queryState.timeField,
  range: normalizeRange(queryState.range),
  platformCodes: [...queryState.platformCodes],
  accountIds: [...queryState.accountIds],
  templateTypes: [...queryState.templateTypes],
  taskIds: [...queryState.taskIds],
  contentSources: [...queryState.contentSources],
  producers: [...queryState.producers],
  effectSyncStatuses: [...queryState.effectSyncStatuses],
  groupBy: [...queryState.groupBy],
  granularity: queryState.granularity,
  topMetric: queryState.topMetric,
});

const buildProductionQueryStateFromForm = (formValues, previousState) => ({
  timeField: normalizeAllowedValue(
    formValues.timeField,
    PRODUCTION_TIME_FIELD_OPTIONS.map((item) => item.value),
    'created_at'
  ),
  range: normalizeRange(formValues.range),
  contentTypes: ensureStringArray(formValues.contentTypes),
  contentSources: ensureStringArray(formValues.contentSources),
  producers: ensureStringArray(formValues.producers),
  groupBy: normalizeAllowedList(
    formValues.groupBy,
    PRODUCTION_GROUP_OPTIONS.map((item) => item.value),
    ['content_type'],
    2
  ),
  granularity: normalizeAllowedValue(
    formValues.granularity,
    GRANULARITY_OPTIONS.map((item) => item.value),
    'day'
  ),
  topBy: normalizeAllowedValue(
    formValues.topBy,
    PRODUCTION_TOP_OPTIONS.map((item) => item.value),
    'producer'
  ),
  page: 1,
  pageSize: previousState?.pageSize || DEFAULT_PAGE_SIZE,
});

const buildPublishQueryStateFromForm = (formValues, previousState) => ({
  timeField: normalizeAllowedValue(
    formValues.timeField,
    PUBLISH_TIME_FIELD_OPTIONS.map((item) => item.value),
    'publish_time'
  ),
  range: normalizeRange(formValues.range),
  platformCodes: ensureStringArray(formValues.platformCodes),
  accountIds: ensureStringArray(formValues.accountIds),
  statuses: ensureStringArray(formValues.statuses),
  templateTypes: ensureStringArray(formValues.templateTypes),
  publisherUserIds: ensureStringArray(formValues.publisherUserIds),
  taskIds: ensureStringArray(formValues.taskIds),
  groupBy: normalizeAllowedList(
    formValues.groupBy,
    PUBLISH_GROUP_OPTIONS.map((item) => item.value),
    ['account_id', 'platform_code'],
    3
  ),
  granularity: normalizeAllowedValue(
    formValues.granularity,
    GRANULARITY_OPTIONS.map((item) => item.value),
    'day'
  ),
  topBy: normalizeAllowedValue(
    formValues.topBy,
    PUBLISH_TOP_OPTIONS.map((item) => item.value),
    'account_id'
  ),
  page: 1,
  pageSize: previousState?.pageSize || DEFAULT_PAGE_SIZE,
});

const buildEffectQueryStateFromForm = (formValues, previousState) => {
  const mode = normalizeAllowedValue(
    formValues.mode,
    EFFECT_MODE_OPTIONS.map((item) => item.value),
    'current'
  );
  const timeFieldOptions = mode === 'delta' ? EFFECT_DELTA_TIME_FIELD_OPTIONS : EFFECT_CURRENT_TIME_FIELD_OPTIONS;
  return {
    mode,
    timeField: normalizeAllowedValue(
      formValues.timeField,
      timeFieldOptions.map((item) => item.value),
      mode === 'delta' ? 'business_date' : 'publish_time'
    ),
    range: normalizeRange(formValues.range),
    platformCodes: ensureStringArray(formValues.platformCodes),
    accountIds: ensureStringArray(formValues.accountIds),
    templateTypes: ensureStringArray(formValues.templateTypes),
    taskIds: ensureStringArray(formValues.taskIds),
    contentSources: ensureStringArray(formValues.contentSources),
    producers: ensureStringArray(formValues.producers),
    effectSyncStatuses: ensureStringArray(formValues.effectSyncStatuses),
    groupBy: normalizeAllowedList(
      formValues.groupBy,
      EFFECT_GROUP_OPTIONS.map((item) => item.value),
      ['platform_code', 'template_type'],
      3
    ),
    granularity: normalizeAllowedValue(
      formValues.granularity,
      GRANULARITY_OPTIONS.map((item) => item.value),
      'day'
    ),
    topMetric: normalizeAllowedValue(
      formValues.topMetric,
      EFFECT_METRIC_OPTIONS.map((item) => item.value),
      'view_count'
    ),
    page: 1,
    pageSize: previousState?.pageSize || DEFAULT_PAGE_SIZE,
  };
};

const buildBaseTimeRangePayload = (queryState) => {
  const [start, end] = normalizeRange(queryState.range);
  return {
    field: queryState.timeField,
    from: formatDateOnly(start),
    to: formatDateOnly(end),
    timezone: SHANGHAI_TIMEZONE,
  };
};

const buildProductionFiltersPayload = (queryState) => {
  const filters = {};
  if (queryState.contentTypes.length > 0) filters.content_type = queryState.contentTypes;
  if (queryState.contentSources.length > 0) filters.content_source = queryState.contentSources;
  if (queryState.producers.length > 0) filters.producer = queryState.producers;
  return filters;
};

const buildPublishFiltersPayload = (queryState) => {
  const filters = {};
  if (queryState.platformCodes.length > 0) filters.platform_code = queryState.platformCodes;
  if (queryState.accountIds.length > 0) filters.account_id = queryState.accountIds;
  if (queryState.statuses.length > 0) filters.status = queryState.statuses;
  if (queryState.templateTypes.length > 0) filters.template_type = queryState.templateTypes;
  if (queryState.publisherUserIds.length > 0) filters.publisher_user_id = queryState.publisherUserIds;
  if (queryState.taskIds.length > 0) filters.task_id = queryState.taskIds;
  return filters;
};

const buildProductionOverviewPayload = (queryState) => ({
  time_range: buildBaseTimeRangePayload(queryState),
  filters: buildProductionFiltersPayload(queryState),
});

const buildProductionQueryPayload = (queryState) => ({
  time_range: buildBaseTimeRangePayload(queryState),
  filters: buildProductionFiltersPayload(queryState),
  group_by: queryState.groupBy,
  granularity: queryState.granularity,
  top_by: queryState.topBy,
  top_limit: 10,
  sort: {
    field: 'content_total',
    order: 'desc',
  },
  page: queryState.page,
  page_size: queryState.pageSize,
});

const buildPublishOverviewPayload = (queryState) => ({
  time_range: buildBaseTimeRangePayload(queryState),
  filters: buildPublishFiltersPayload(queryState),
});

const buildPublishQueryPayload = (queryState) => ({
  time_range: buildBaseTimeRangePayload(queryState),
  filters: buildPublishFiltersPayload(queryState),
  group_by: queryState.groupBy,
  granularity: queryState.granularity,
  top_by: queryState.topBy,
  top_limit: 10,
  sort: {
    field: 'publish_total',
    order: 'desc',
  },
  page: queryState.page,
  page_size: queryState.pageSize,
});

const buildPublishFailureReasonsPayload = (queryState) => ({
  time_range: buildBaseTimeRangePayload(queryState),
  filters: buildPublishFiltersPayload(queryState),
  top_limit: 10,
});

const buildEffectFiltersPayload = (queryState) => {
  const filters = {};
  if (queryState.platformCodes.length > 0) filters.platform_code = queryState.platformCodes;
  if (queryState.accountIds.length > 0) filters.account_id = queryState.accountIds;
  if (queryState.templateTypes.length > 0) filters.template_type = queryState.templateTypes;
  if (queryState.taskIds.length > 0) filters.task_id = queryState.taskIds;
  if (queryState.contentSources.length > 0) filters.content_source = queryState.contentSources;
  if (queryState.producers.length > 0) filters.producer = queryState.producers;
  if (queryState.effectSyncStatuses.length > 0) filters.effect_sync_status = queryState.effectSyncStatuses;
  return filters;
};

const buildEffectOverviewPayload = (queryState) => ({
  mode: queryState.mode,
  time_range: buildBaseTimeRangePayload(queryState),
  filters: buildEffectFiltersPayload(queryState),
});

const buildEffectQueryPayload = (queryState) => ({
  mode: queryState.mode,
  time_range: buildBaseTimeRangePayload(queryState),
  filters: buildEffectFiltersPayload(queryState),
  group_by: queryState.groupBy,
  granularity: queryState.granularity,
  top_metric: queryState.topMetric,
  top_limit: 10,
  sort: {
    field: queryState.topMetric,
    order: 'desc',
  },
  page: queryState.page,
  page_size: queryState.pageSize,
});

const getEffectTimeFieldOptions = (mode) => (mode === 'delta' ? EFFECT_DELTA_TIME_FIELD_OPTIONS : EFFECT_CURRENT_TIME_FIELD_OPTIONS);

const getEffectMetricLabel = (metric, mode = 'current') => {
  const baseLabel = getTopLabelText(metric, EFFECT_METRIC_OPTIONS);
  return mode === 'delta' ? `新增${baseLabel}` : baseLabel;
};

const formatDateTimeText = (value) => formatShanghaiDateTime(value, '-');

const setArrayParam = (searchParams, key, values) => {
  if (values.length > 0) searchParams.set(key, values.join(','));
};

const buildStatisticsSearch = (activeTab, productionQueryState, publishQueryState, effectQueryState) => {
  const searchParams = new URLSearchParams();
  searchParams.set('tab', activeTab);

  const [prodStart, prodEnd] = normalizeRange(productionQueryState.range);
  searchParams.set('prod_time_field', productionQueryState.timeField);
  searchParams.set('prod_from', formatDateOnly(prodStart));
  searchParams.set('prod_to', formatDateOnly(prodEnd));
  setArrayParam(searchParams, 'prod_content_type', productionQueryState.contentTypes);
  setArrayParam(searchParams, 'prod_content_source', productionQueryState.contentSources);
  setArrayParam(searchParams, 'prod_producer', productionQueryState.producers);
  searchParams.set('prod_group_by', productionQueryState.groupBy.join(','));
  searchParams.set('prod_granularity', productionQueryState.granularity);
  searchParams.set('prod_top_by', productionQueryState.topBy);
  searchParams.set('prod_page', String(productionQueryState.page));
  searchParams.set('prod_page_size', String(productionQueryState.pageSize));

  const [pubStart, pubEnd] = normalizeRange(publishQueryState.range);
  searchParams.set('pub_time_field', publishQueryState.timeField);
  searchParams.set('pub_from', formatDateOnly(pubStart));
  searchParams.set('pub_to', formatDateOnly(pubEnd));
  setArrayParam(searchParams, 'pub_platform_code', publishQueryState.platformCodes);
  setArrayParam(searchParams, 'pub_account_id', publishQueryState.accountIds);
  setArrayParam(searchParams, 'pub_status', publishQueryState.statuses);
  setArrayParam(searchParams, 'pub_template_type', publishQueryState.templateTypes);
  setArrayParam(searchParams, 'pub_publisher_user_id', publishQueryState.publisherUserIds);
  setArrayParam(searchParams, 'pub_task_id', publishQueryState.taskIds);
  searchParams.set('pub_group_by', publishQueryState.groupBy.join(','));
  searchParams.set('pub_granularity', publishQueryState.granularity);
  searchParams.set('pub_top_by', publishQueryState.topBy);
  searchParams.set('pub_page', String(publishQueryState.page));
  searchParams.set('pub_page_size', String(publishQueryState.pageSize));

  const [effStart, effEnd] = normalizeRange(effectQueryState.range);
  searchParams.set('eff_mode', effectQueryState.mode);
  searchParams.set('eff_time_field', effectQueryState.timeField);
  searchParams.set('eff_from', formatDateOnly(effStart));
  searchParams.set('eff_to', formatDateOnly(effEnd));
  setArrayParam(searchParams, 'eff_platform_code', effectQueryState.platformCodes);
  setArrayParam(searchParams, 'eff_account_id', effectQueryState.accountIds);
  setArrayParam(searchParams, 'eff_template_type', effectQueryState.templateTypes);
  setArrayParam(searchParams, 'eff_task_id', effectQueryState.taskIds);
  setArrayParam(searchParams, 'eff_content_source', effectQueryState.contentSources);
  setArrayParam(searchParams, 'eff_producer', effectQueryState.producers);
  setArrayParam(searchParams, 'eff_sync_status', effectQueryState.effectSyncStatuses);
  searchParams.set('eff_group_by', effectQueryState.groupBy.join(','));
  searchParams.set('eff_granularity', effectQueryState.granularity);
  searchParams.set('eff_top_metric', effectQueryState.topMetric);
  searchParams.set('eff_page', String(effectQueryState.page));
  searchParams.set('eff_page_size', String(effectQueryState.pageSize));

  const search = searchParams.toString();
  return search ? `?${search}` : '';
};

const getGroupLabelText = (items, labelMap) => {
  if (!Array.isArray(items) || items.length === 0) return '默认';
  return items.map((item) => labelMap[item] || item).join(' / ');
};

const getTopLabelText = (value, options) => {
  const matched = options.find((item) => item.value === value);
  return matched?.label || value || '-';
};

const buildOptionList = (items = []) => (
  Array.isArray(items)
    ? items.map((item) => ({
      label: item?.label || item?.value || '-',
      value: item?.value || '',
    })).filter((item) => item.value)
    : []
);

const buildAccountOptionList = (items = []) => (
  Array.isArray(items)
    ? items.map((item) => ({
      label: item?.platform_code ? `${item.label} (${item.platform_code})` : item?.label || item?.value || '-',
      value: item?.value || '',
    })).filter((item) => item.value)
    : []
);

const createCsvCell = (value) => {
  const text = String(value ?? '');
  const escaped = text.replace(/"/g, '""');
  return /[",\n]/.test(escaped) ? `"${escaped}"` : escaped;
};

const downloadRowsAsCsv = ({ columns, rows, filename }) => {
  const header = columns.map((column) => createCsvCell(column.title)).join(',');
  const body = rows.map((row) => columns.map((column) => {
    if (typeof column.csvValue === 'function') {
      return createCsvCell(column.csvValue(row));
    }
    return createCsvCell(row[column.dataIndex]);
  }).join(',')).join('\n');
  const content = [header, body].filter(Boolean).join('\n');
  const blob = new Blob(['\uFEFF', content], {
    type: 'text/csv;charset=utf-8;',
  });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
};

const collectAllTableRows = async (queryState, payloadBuilder, fetcher) => {
  const firstResponse = await fetcher(payloadBuilder({
    ...queryState,
    page: 1,
    pageSize: EXPORT_PAGE_SIZE,
  }));
  const firstTable = firstResponse?.data?.table || {};
  const total = Number(firstTable.total || 0);
  const items = Array.isArray(firstTable.items) ? [...firstTable.items] : [];
  if (total <= EXPORT_PAGE_SIZE) return items;

  const totalPages = Math.ceil(total / EXPORT_PAGE_SIZE);
  const requests = [];
  for (let page = 2; page <= totalPages; page += 1) {
    requests.push(fetcher(payloadBuilder({
      ...queryState,
      page,
      pageSize: EXPORT_PAGE_SIZE,
    })));
  }
  const responses = await Promise.all(requests);
  responses.forEach((response) => {
    const pageItems = response?.data?.table?.items;
    if (Array.isArray(pageItems)) items.push(...pageItems);
  });
  return items.slice(0, total);
};

const buildProductionTableColumns = (groupBy) => {
  const dimensionColumns = groupBy.map((dimension) => ({
    title: PRODUCTION_DIMENSION_LABELS[dimension] || dimension,
    dataIndex: dimension,
    key: dimension,
    render: (value) => value || '-',
    csvValue: (record) => record?.[dimension] || '',
  }));
  return [
    ...dimensionColumns,
    {
      title: '内容数',
      dataIndex: 'content_total',
      key: 'content_total',
      csvValue: (record) => record?.content_total ?? 0,
    },
  ];
};

const buildPublishTableColumns = (groupBy) => {
  const dimensionColumns = groupBy.map((dimension) => {
    if (dimension === 'account_id') {
      return {
        title: PUBLISH_DIMENSION_LABELS[dimension],
        dataIndex: dimension,
        key: dimension,
        render: (value, record) => record?.account_name || value || '-',
        csvValue: (record) => record?.account_name || record?.account_id || '',
      };
    }
    if (dimension === 'publisher_user_id') {
      return {
        title: PUBLISH_DIMENSION_LABELS[dimension],
        dataIndex: dimension,
        key: dimension,
        render: (value, record) => record?.publisher_user_name || value || '-',
        csvValue: (record) => record?.publisher_user_name || record?.publisher_user_id || '',
      };
    }
    return {
      title: PUBLISH_DIMENSION_LABELS[dimension] || dimension,
      dataIndex: dimension,
      key: dimension,
      render: (value) => value || '-',
      csvValue: (record) => record?.[dimension] || '',
    };
  });

  return [
    ...dimensionColumns,
    {
      title: '发布总数',
      dataIndex: 'publish_total',
      key: 'publish_total',
      csvValue: (record) => record?.publish_total ?? 0,
    },
    {
      title: '成功',
      dataIndex: 'publish_success_total',
      key: 'publish_success_total',
      csvValue: (record) => record?.publish_success_total ?? 0,
    },
    {
      title: '失败',
      dataIndex: 'publish_failed_total',
      key: 'publish_failed_total',
      csvValue: (record) => record?.publish_failed_total ?? 0,
    },
    {
      title: '发布中',
      dataIndex: 'publish_publishing_total',
      key: 'publish_publishing_total',
      csvValue: (record) => record?.publish_publishing_total ?? 0,
    },
    {
      title: '成功率',
      dataIndex: 'publish_success_rate',
      key: 'publish_success_rate',
      render: (value) => formatPercent(value),
      csvValue: (record) => formatPercent(record?.publish_success_rate),
    },
  ];
};

const buildEffectTableColumns = (groupBy, mode) => {
  const dimensionColumns = groupBy.map((dimension) => {
    if (dimension === 'account_id') {
      return {
        title: EFFECT_DIMENSION_LABELS[dimension],
        dataIndex: dimension,
        key: dimension,
        render: (value, record) => record?.account_name || value || '-',
        csvValue: (record) => record?.account_name || record?.account_id || '',
      };
    }
    return {
      title: EFFECT_DIMENSION_LABELS[dimension] || dimension,
      dataIndex: dimension,
      key: dimension,
      render: (value) => value || '-',
      csvValue: (record) => record?.[dimension] || '',
    };
  });

  return [
    ...dimensionColumns,
    {
      title: '作品数',
      dataIndex: 'publish_total',
      key: 'publish_total',
      csvValue: (record) => record?.publish_total ?? 0,
    },
    ...EFFECT_METRIC_OPTIONS.map((option) => ({
      title: getEffectMetricLabel(option.value, mode),
      dataIndex: option.value,
      key: option.value,
      csvValue: (record) => record?.[option.value] ?? 0,
    })),
  ];
};

const buildEffectTopContentColumns = (mode, topMetric) => [
  { title: '排名', dataIndex: 'rank', key: 'rank', width: 80 },
  { title: '标题', dataIndex: 'title', key: 'title', render: (value) => value || '-' },
  { title: '平台', dataIndex: 'platform_code', key: 'platform_code', render: (value, record) => record?.platform_display || value || '-' },
  { title: '账号', dataIndex: 'account_name', key: 'account_name', render: (value, record) => value || record?.account_id || '-' },
  { title: '内容类型', dataIndex: 'template_type', key: 'template_type', render: (value) => value || '-' },
  {
    title: getEffectMetricLabel(topMetric, mode),
    dataIndex: topMetric,
    key: topMetric,
  },
  {
    title: '最近同步',
    dataIndex: 'effect_last_synced_at',
    key: 'effect_last_synced_at',
    render: (value) => formatDateTimeText(value),
  },
];

const Statistics = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const activeTab = parseTabFromSearch(location.search);

  const [productionForm] = Form.useForm();
  const [publishForm] = Form.useForm();
  const [effectForm] = Form.useForm();
  const [productionQueryState, setProductionQueryState] = useState(() => parseProductionQueryFromSearch(location.search));
  const [publishQueryState, setPublishQueryState] = useState(() => parsePublishQueryFromSearch(location.search));
  const [effectQueryState, setEffectQueryState] = useState(() => parseEffectQueryFromSearch(location.search));
  const effectMode = Form.useWatch('mode', effectForm) || effectQueryState.mode;
  const [productionState, setProductionState] = useState(createPanelState);
  const [publishState, setPublishState] = useState(createPanelState);
  const [effectState, setEffectState] = useState(createPanelState);
  const [mountedTabs, setMountedTabs] = useState(() => ({
    production: activeTab === 'production',
    publish: activeTab === 'publish',
    effect: activeTab === 'effect',
  }));

  useEffect(() => {
    const allowedFields = getEffectTimeFieldOptions(effectMode).map((item) => item.value);
    const currentTimeField = effectForm.getFieldValue('timeField');
    if (allowedFields.includes(currentTimeField)) return;
    effectForm.setFieldValue('timeField', effectMode === 'delta' ? 'business_date' : 'publish_time');
  }, [effectForm, effectMode]);

  const syncUrl = useCallback((nextTab, nextProductionQueryState, nextPublishQueryState, nextEffectQueryState) => {
    navigate({
      pathname: location.pathname,
      search: buildStatisticsSearch(nextTab, nextProductionQueryState, nextPublishQueryState, nextEffectQueryState),
    }, { replace: true });
  }, [location.pathname, navigate]);

  const loadProductionOptions = useCallback(async (force = false) => {
    if (productionState.optionsLoading) return;
    if (productionState.optionsAttempted && !force) return;
    setProductionState((prev) => ({
      ...prev,
      optionsLoading: true,
      optionsAttempted: true,
      optionsError: '',
    }));
    try {
      const response = await getStatisticsFilterOptions({ module: 'production' });
      setProductionState((prev) => ({
        ...prev,
        optionsLoading: false,
        optionsError: '',
        options: response?.data || {},
      }));
    } catch (error) {
      setProductionState((prev) => ({
        ...prev,
        optionsLoading: false,
        optionsError: error.message || '获取生产统计筛选项失败',
      }));
    }
  }, [productionState.optionsAttempted, productionState.optionsLoading]);

  const loadPublishOptions = useCallback(async (force = false) => {
    if (publishState.optionsLoading) return;
    if (publishState.optionsAttempted && !force) return;
    setPublishState((prev) => ({
      ...prev,
      optionsLoading: true,
      optionsAttempted: true,
      optionsError: '',
    }));
    try {
      const response = await getStatisticsFilterOptions({ module: 'publish' });
      setPublishState((prev) => ({
        ...prev,
        optionsLoading: false,
        optionsError: '',
        options: response?.data || {},
      }));
    } catch (error) {
      setPublishState((prev) => ({
        ...prev,
        optionsLoading: false,
        optionsError: error.message || '获取发布统计筛选项失败',
      }));
    }
  }, [publishState.optionsAttempted, publishState.optionsLoading]);

  const loadEffectOptions = useCallback(async (force = false) => {
    if (effectState.optionsLoading) return;
    if (effectState.optionsAttempted && !force) return;
    setEffectState((prev) => ({
      ...prev,
      optionsLoading: true,
      optionsAttempted: true,
      optionsError: '',
    }));
    try {
      const response = await getStatisticsFilterOptions({ module: 'effect' });
      setEffectState((prev) => ({
        ...prev,
        optionsLoading: false,
        optionsError: '',
        options: response?.data || {},
      }));
    } catch (error) {
      setEffectState((prev) => ({
        ...prev,
        optionsLoading: false,
        optionsError: error.message || '获取效果统计筛选项失败',
      }));
    }
  }, [effectState.optionsAttempted, effectState.optionsLoading]);

  const loadProduction = useCallback(async (queryState) => {
    setProductionState((prev) => ({
      ...prev,
      loading: true,
      loaded: true,
      error: '',
    }));
    try {
      const [overviewResponse, queryResponse] = await Promise.all([
        getProductionStatisticsOverview(buildProductionOverviewPayload(queryState)),
        getProductionStatisticsQuery(buildProductionQueryPayload(queryState)),
      ]);
      setProductionState((prev) => ({
        ...prev,
        loading: false,
        error: '',
        overview: overviewResponse?.data || null,
        query: queryResponse?.data || null,
        extra: {
          queryState,
        },
      }));
    } catch (error) {
      setProductionState((prev) => ({
        ...prev,
        loading: false,
        error: error.message || '获取生产统计失败',
      }));
    }
  }, []);

  const loadPublish = useCallback(async (queryState) => {
    setPublishState((prev) => ({
      ...prev,
      loading: true,
      loaded: true,
      error: '',
    }));
    try {
      const [overviewResponse, queryResponse, failureReasonsResponse] = await Promise.all([
        getPublishStatisticsOverview(buildPublishOverviewPayload(queryState)),
        getPublishStatisticsQuery(buildPublishQueryPayload(queryState)),
        getPublishFailureReasons(buildPublishFailureReasonsPayload(queryState)),
      ]);
      setPublishState((prev) => ({
        ...prev,
        loading: false,
        error: '',
        overview: overviewResponse?.data || null,
        query: queryResponse?.data || null,
        extra: {
          queryState,
          failureReasons: failureReasonsResponse?.data || null,
        },
      }));
    } catch (error) {
      setPublishState((prev) => ({
        ...prev,
        loading: false,
        error: error.message || '获取发布统计失败',
      }));
    }
  }, []);

  const loadEffect = useCallback(async (queryState) => {
    setEffectState((prev) => ({
      ...prev,
      loading: true,
      loaded: true,
      error: '',
    }));
    try {
      const [overviewResponse, queryResponse] = await Promise.all([
        getEffectStatisticsOverview(buildEffectOverviewPayload(queryState)),
        getEffectStatisticsQuery(buildEffectQueryPayload(queryState)),
      ]);
      setEffectState((prev) => ({
        ...prev,
        loading: false,
        error: '',
        overview: overviewResponse?.data || null,
        query: queryResponse?.data || null,
        extra: {
          queryState,
        },
      }));
    } catch (error) {
      setEffectState((prev) => ({
        ...prev,
        loading: false,
        error: error.message || '获取效果统计失败',
      }));
    }
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(location.search || '');
    const tab = params.get('tab');
    if (!TAB_OPTIONS.includes(tab)) {
      syncUrl('production', productionQueryState, publishQueryState, effectQueryState);
    }
  }, [location.search, productionQueryState, publishQueryState, effectQueryState, syncUrl]);

  useEffect(() => {
    setMountedTabs((prev) => ({ ...prev, [activeTab]: true }));
  }, [activeTab]);

  useEffect(() => {
    if (activeTab === 'production') {
      void loadProductionOptions();
      if (!productionState.loaded && !productionState.loading) {
        void loadProduction(productionQueryState);
      }
    }
    if (activeTab === 'publish') {
      void loadPublishOptions();
      if (!publishState.loaded && !publishState.loading) {
        void loadPublish(publishQueryState);
      }
    }
    if (activeTab === 'effect') {
      void loadEffectOptions();
      if (!effectState.loaded && !effectState.loading) {
        void loadEffect(effectQueryState);
      }
    }
  }, [
    activeTab,
    productionQueryState,
    publishQueryState,
    effectQueryState,
    productionState.loaded,
    productionState.loading,
    publishState.loaded,
    publishState.loading,
    effectState.loaded,
    effectState.loading,
    loadProductionOptions,
    loadPublishOptions,
    loadEffectOptions,
    loadProduction,
    loadPublish,
    loadEffect,
  ]);

  const handleTabChange = (tab) => {
    syncUrl(tab, productionQueryState, publishQueryState, effectQueryState);
  };

  const handleProductionSearch = async () => {
    try {
      const values = await productionForm.validateFields();
      const nextQueryState = buildProductionQueryStateFromForm(values, productionQueryState);
      setProductionQueryState(nextQueryState);
      syncUrl('production', nextQueryState, publishQueryState, effectQueryState);
      await loadProduction(nextQueryState);
    } catch (error) {
      if (error?.errorFields) return;
      message.error(error.message || '查询生产统计失败');
    }
  };

  const handlePublishSearch = async () => {
    try {
      const values = await publishForm.validateFields();
      const nextQueryState = buildPublishQueryStateFromForm(values, publishQueryState);
      setPublishQueryState(nextQueryState);
      syncUrl('publish', productionQueryState, nextQueryState, effectQueryState);
      await loadPublish(nextQueryState);
    } catch (error) {
      if (error?.errorFields) return;
      message.error(error.message || '查询发布统计失败');
    }
  };

  const handleEffectSearch = async () => {
    try {
      const values = await effectForm.validateFields();
      const nextQueryState = buildEffectQueryStateFromForm(values, effectQueryState);
      setEffectQueryState(nextQueryState);
      syncUrl('effect', productionQueryState, publishQueryState, nextQueryState);
      await loadEffect(nextQueryState);
    } catch (error) {
      if (error?.errorFields) return;
      message.error(error.message || '查询效果统计失败');
    }
  };

  const handleProductionReset = async () => {
    const nextQueryState = createDefaultProductionQuery();
    productionForm.setFieldsValue(buildProductionFormValues(nextQueryState));
    setProductionQueryState(nextQueryState);
    syncUrl('production', nextQueryState, publishQueryState, effectQueryState);
    await loadProduction(nextQueryState);
  };

  const handlePublishReset = async () => {
    const nextQueryState = createDefaultPublishQuery();
    publishForm.setFieldsValue(buildPublishFormValues(nextQueryState));
    setPublishQueryState(nextQueryState);
    syncUrl('publish', productionQueryState, nextQueryState, effectQueryState);
    await loadPublish(nextQueryState);
  };

  const handleEffectReset = async () => {
    const nextQueryState = createDefaultEffectQuery();
    effectForm.setFieldsValue(buildEffectFormValues(nextQueryState));
    setEffectQueryState(nextQueryState);
    syncUrl('effect', productionQueryState, publishQueryState, nextQueryState);
    await loadEffect(nextQueryState);
  };

  const handleProductionPageChange = async (page, pageSize) => {
    const nextQueryState = {
      ...productionQueryState,
      page,
      pageSize,
    };
    setProductionQueryState(nextQueryState);
    syncUrl('production', nextQueryState, publishQueryState, effectQueryState);
    await loadProduction(nextQueryState);
  };

  const handlePublishPageChange = async (page, pageSize) => {
    const nextQueryState = {
      ...publishQueryState,
      page,
      pageSize,
    };
    setPublishQueryState(nextQueryState);
    syncUrl('publish', productionQueryState, nextQueryState, effectQueryState);
    await loadPublish(nextQueryState);
  };

  const handleEffectPageChange = async (page, pageSize) => {
    const nextQueryState = {
      ...effectQueryState,
      page,
      pageSize,
    };
    setEffectQueryState(nextQueryState);
    syncUrl('effect', productionQueryState, publishQueryState, nextQueryState);
    await loadEffect(nextQueryState);
  };

  const handleProductionExport = async () => {
    const total = Number(productionState.query?.table?.total || 0);
    if (total <= 0) {
      message.info('当前没有可导出的生产统计数据');
      return;
    }
    setProductionState((prev) => ({ ...prev, exporting: true }));
    try {
      const rows = await collectAllTableRows(
        productionQueryState,
        buildProductionQueryPayload,
        getProductionStatisticsQuery
      );
      downloadRowsAsCsv({
        columns: buildProductionTableColumns(productionQueryState.groupBy),
        rows,
        filename: `publishy_生产统计_${dayjs().format('YYYYMMDD_HHmmss')}.csv`,
      });
      message.success(`已导出 ${rows.length} 条生产统计记录`);
    } catch (error) {
      message.error(error.message || '导出生产统计失败');
    } finally {
      setProductionState((prev) => ({ ...prev, exporting: false }));
    }
  };

  const handlePublishExport = async () => {
    const total = Number(publishState.query?.table?.total || 0);
    if (total <= 0) {
      message.info('当前没有可导出的发布统计数据');
      return;
    }
    setPublishState((prev) => ({ ...prev, exporting: true }));
    try {
      const rows = await collectAllTableRows(
        publishQueryState,
        buildPublishQueryPayload,
        getPublishStatisticsQuery
      );
      downloadRowsAsCsv({
        columns: buildPublishTableColumns(publishQueryState.groupBy),
        rows,
        filename: `publishy_发布统计_${dayjs().format('YYYYMMDD_HHmmss')}.csv`,
      });
      message.success(`已导出 ${rows.length} 条发布统计记录`);
    } catch (error) {
      message.error(error.message || '导出发布统计失败');
    } finally {
      setPublishState((prev) => ({ ...prev, exporting: false }));
    }
  };

  const handleEffectExport = async () => {
    const total = Number(effectState.query?.table?.total || 0);
    if (total <= 0) {
      message.info('当前没有可导出的效果统计数据');
      return;
    }
    setEffectState((prev) => ({ ...prev, exporting: true }));
    try {
      const rows = await collectAllTableRows(
        effectQueryState,
        buildEffectQueryPayload,
        getEffectStatisticsQuery
      );
      downloadRowsAsCsv({
        columns: buildEffectTableColumns(effectQueryState.groupBy, effectQueryState.mode),
        rows,
        filename: `publishy_效果统计_${dayjs().format('YYYYMMDD_HHmmss')}.csv`,
      });
      message.success(`已导出 ${rows.length} 条效果统计记录`);
    } catch (error) {
      message.error(error.message || '导出效果统计失败');
    } finally {
      setEffectState((prev) => ({ ...prev, exporting: false }));
    }
  };

  const productionTableColumns = buildProductionTableColumns(productionQueryState.groupBy);
  const publishTableColumns = buildPublishTableColumns(publishQueryState.groupBy);
  const effectTableColumns = buildEffectTableColumns(effectQueryState.groupBy, effectQueryState.mode);
  const effectTopContentColumns = buildEffectTopContentColumns(effectQueryState.mode, effectQueryState.topMetric);
  const productionTable = productionState.query?.table || {};
  const publishTable = publishState.query?.table || {};
  const effectTable = effectState.query?.table || {};

  const tabItems = [
    { key: 'production', label: '生产统计' },
    { key: 'publish', label: '发布统计' },
    { key: 'effect', label: '效果统计' },
  ];

  return (
    <div className="page-shell statistics-page">
      <Card className="page-card statistics-page__card">
        <div className="statistics-page__header">
          <div>
            <Title level={3} className="statistics-page__title">数据统计</Title>
            <Paragraph className="statistics-page__subtitle">
              统一查看内容生产、发布执行与后续效果表现。当前阶段已支持生产统计、发布统计、效果统计的筛选查询与 CSV 导出。
            </Paragraph>
          </div>
          <Space wrap>
            {activeTab === 'production' && (
              <Button icon={<ReloadOutlined />} onClick={() => void loadProduction(productionQueryState)}>
                刷新生产统计
              </Button>
            )}
            {activeTab === 'publish' && (
              <Button icon={<ReloadOutlined />} onClick={() => void loadPublish(publishQueryState)}>
                刷新发布统计
              </Button>
            )}
            {activeTab === 'effect' && (
              <Button icon={<ReloadOutlined />} onClick={() => void loadEffect(effectQueryState)}>
                刷新效果统计
              </Button>
            )}
          </Space>
        </div>

        <Tabs activeKey={activeTab} items={tabItems} onChange={handleTabChange} />

        {mountedTabs.production && (
          <div style={{ display: activeTab === 'production' ? 'block' : 'none' }}>
            <Space direction="vertical" size={16} style={{ width: '100%' }}>
              <Card
                title="筛选条件"
                className="statistics-page__section-card"
                extra={(
                  <Space wrap>
                    <Button icon={<ReloadOutlined />} onClick={() => void loadProductionOptions(true)} loading={productionState.optionsLoading}>
                      重载筛选项
                    </Button>
                    <Button onClick={() => void handleProductionReset()}>
                      重置
                    </Button>
                    <Button type="primary" icon={<SearchOutlined />} onClick={() => void handleProductionSearch()}>
                      查询
                    </Button>
                  </Space>
                )}
              >
                {productionState.optionsError && (
                  <Alert
                    className="statistics-page__alert"
                    type="warning"
                    showIcon
                    message={productionState.optionsError}
                  />
                )}
                <Form
                  form={productionForm}
                  layout="vertical"
                  initialValues={buildProductionFormValues(productionQueryState)}
                >
                  <Row gutter={[16, 8]}>
                    <Col xs={24} md={12} xl={8}>
                      <Form.Item label="统计时间" name="range">
                        <RangePicker style={{ width: '100%' }} allowClear={false} />
                      </Form.Item>
                    </Col>
                    <Col xs={24} md={12} xl={6}>
                      <Form.Item label="时间字段" name="timeField">
                        <Select options={PRODUCTION_TIME_FIELD_OPTIONS} />
                      </Form.Item>
                    </Col>
                    <Col xs={24} md={12} xl={10}>
                      <Form.Item label="内容类型" name="contentTypes">
                        <Select
                          mode="multiple"
                          allowClear
                          maxTagCount="responsive"
                          loading={productionState.optionsLoading}
                          options={buildOptionList(productionState.options?.content_types)}
                          placeholder="全部内容类型"
                        />
                      </Form.Item>
                    </Col>
                    <Col xs={24} md={12} xl={10}>
                      <Form.Item label="内容来源" name="contentSources">
                        <Select
                          mode="multiple"
                          allowClear
                          maxTagCount="responsive"
                          loading={productionState.optionsLoading}
                          options={buildOptionList(productionState.options?.content_sources)}
                          placeholder="全部内容来源"
                        />
                      </Form.Item>
                    </Col>
                    <Col xs={24} md={12} xl={8}>
                      <Form.Item label="生产器" name="producers">
                        <Select
                          mode="multiple"
                          allowClear
                          maxTagCount="responsive"
                          loading={productionState.optionsLoading}
                          options={buildOptionList(productionState.options?.producers)}
                          placeholder="全部生产器"
                        />
                      </Form.Item>
                    </Col>
                    <Col xs={24} md={12} xl={6}>
                      <Form.Item label="趋势粒度" name="granularity">
                        <Select options={GRANULARITY_OPTIONS} />
                      </Form.Item>
                    </Col>
                    <Col xs={24} md={12} xl={8}>
                      <Form.Item label="分组维度" name="groupBy">
                        <Select
                          mode="multiple"
                          allowClear
                          maxCount={2}
                          maxTagCount="responsive"
                          options={PRODUCTION_GROUP_OPTIONS}
                          placeholder="最多 2 个维度"
                        />
                      </Form.Item>
                    </Col>
                    <Col xs={24} md={12} xl={6}>
                      <Form.Item label="Top 维度" name="topBy">
                        <Select options={PRODUCTION_TOP_OPTIONS} />
                      </Form.Item>
                    </Col>
                  </Row>
                </Form>
              </Card>

              <Alert
                type="info"
                showIcon
                message={`统计周期：${formatDateOnly(productionQueryState.range[0])} 至 ${formatDateOnly(productionQueryState.range[1])}`}
                description={`分组维度：${getGroupLabelText(productionQueryState.groupBy, PRODUCTION_DIMENSION_LABELS)}；趋势粒度：${getTopLabelText(productionQueryState.granularity, GRANULARITY_OPTIONS)}；Top 维度：${getTopLabelText(productionQueryState.topBy, PRODUCTION_TOP_OPTIONS)}`}
              />

              {productionState.error && (
                <Alert type="error" showIcon message={productionState.error} />
              )}

              <Spin spinning={productionState.loading}>
                <Space direction="vertical" size={16} style={{ width: '100%' }}>
                  <Row gutter={[16, 16]}>
                    <MetricCard title="内容总数" value={productionState.overview?.content_total || 0} />
                    <MetricCard title="AI 生成" value={productionState.overview?.ai_generated_total || 0} />
                    <MetricCard title="手工导入" value={productionState.overview?.manual_total || 0} />
                    <MetricCard title="抓取内容" value={productionState.overview?.crawled_total || 0} />
                    <MetricCard title="生产器数" value={productionState.overview?.producer_distinct_total || 0} />
                    <MetricCard title="生产 Run 成功率" value={formatPercent(productionState.overview?.producer_run_success_rate)} text />
                  </Row>

                  <Card title="生产趋势" className="statistics-page__section-card">
                    <Table
                      size="small"
                      pagination={false}
                      columns={[
                        { title: '日期', dataIndex: 'date', key: 'date' },
                        { title: '内容数', dataIndex: 'content_total', key: 'content_total' },
                      ]}
                      dataSource={productionState.query?.trend || []}
                      rowKey={(record) => `${record.date || 'date'}-${record.content_total || 0}`}
                      locale={{ emptyText: <Empty description="暂无数据" image={Empty.PRESENTED_IMAGE_SIMPLE} /> }}
                    />
                  </Card>

                  <Card
                    title="分组统计"
                    className="statistics-page__section-card"
                    extra={(
                      <Space wrap>
                        <Text type="secondary">{`共 ${productionTable.total || 0} 条`}</Text>
                        <Button
                          icon={<DownloadOutlined />}
                          onClick={() => void handleProductionExport()}
                          loading={productionState.exporting}
                          disabled={!productionTable.total}
                        >
                          导出 CSV
                        </Button>
                      </Space>
                    )}
                  >
                    <Table
                      size="small"
                      columns={productionTableColumns}
                      dataSource={productionTable.items || []}
                      rowKey={(record, index) => `${record.content_total || 0}-${index}`}
                      pagination={{
                        current: productionTable.page || productionQueryState.page,
                        pageSize: productionTable.page_size || productionQueryState.pageSize,
                        total: productionTable.total || 0,
                        showSizeChanger: true,
                        onChange: (page, pageSize) => {
                          void handleProductionPageChange(page, pageSize);
                        },
                      }}
                      locale={{ emptyText: <Empty description="暂无数据" image={Empty.PRESENTED_IMAGE_SIMPLE} /> }}
                      scroll={{ x: 'max-content' }}
                    />
                  </Card>

                  <Card title={`Top 10 - ${getTopLabelText(productionQueryState.topBy, PRODUCTION_TOP_OPTIONS)}`} className="statistics-page__section-card">
                    <Table
                      size="small"
                      pagination={false}
                      columns={[
                        { title: '排名', dataIndex: 'rank', key: 'rank', width: 80 },
                        { title: '维度值', dataIndex: 'rank_label', key: 'rank_label', render: (value) => value || '-' },
                        { title: '内容数', dataIndex: 'content_total', key: 'content_total' },
                      ]}
                      dataSource={productionState.query?.top?.items || []}
                      rowKey={(record) => `${record.rank_key || 'rank'}-${record.rank || 0}`}
                      locale={{ emptyText: <Empty description="暂无数据" image={Empty.PRESENTED_IMAGE_SIMPLE} /> }}
                    />
                  </Card>
                </Space>
              </Spin>
            </Space>
          </div>
        )}

        {mountedTabs.publish && (
          <div style={{ display: activeTab === 'publish' ? 'block' : 'none' }}>
            <Space direction="vertical" size={16} style={{ width: '100%' }}>
              <Card
                title="筛选条件"
                className="statistics-page__section-card"
                extra={(
                  <Space wrap>
                    <Button icon={<ReloadOutlined />} onClick={() => void loadPublishOptions(true)} loading={publishState.optionsLoading}>
                      重载筛选项
                    </Button>
                    <Button onClick={() => void handlePublishReset()}>
                      重置
                    </Button>
                    <Button type="primary" icon={<SearchOutlined />} onClick={() => void handlePublishSearch()}>
                      查询
                    </Button>
                  </Space>
                )}
              >
                {publishState.optionsError && (
                  <Alert
                    className="statistics-page__alert"
                    type="warning"
                    showIcon
                    message={publishState.optionsError}
                  />
                )}
                <Form
                  form={publishForm}
                  layout="vertical"
                  initialValues={buildPublishFormValues(publishQueryState)}
                >
                  <Row gutter={[16, 8]}>
                    <Col xs={24} md={12} xl={8}>
                      <Form.Item label="统计时间" name="range">
                        <RangePicker style={{ width: '100%' }} allowClear={false} />
                      </Form.Item>
                    </Col>
                    <Col xs={24} md={12} xl={6}>
                      <Form.Item label="时间字段" name="timeField">
                        <Select options={PUBLISH_TIME_FIELD_OPTIONS} />
                      </Form.Item>
                    </Col>
                    <Col xs={24} md={12} xl={10}>
                      <Form.Item label="平台" name="platformCodes">
                        <Select
                          mode="multiple"
                          allowClear
                          maxTagCount="responsive"
                          loading={publishState.optionsLoading}
                          options={buildOptionList(publishState.options?.platform_codes)}
                          placeholder="全部平台"
                        />
                      </Form.Item>
                    </Col>
                    <Col xs={24} md={12} xl={10}>
                      <Form.Item label="账号" name="accountIds">
                        <Select
                          mode="multiple"
                          allowClear
                          maxTagCount="responsive"
                          loading={publishState.optionsLoading}
                          options={buildAccountOptionList(publishState.options?.accounts)}
                          placeholder="全部账号"
                        />
                      </Form.Item>
                    </Col>
                    <Col xs={24} md={12} xl={8}>
                      <Form.Item label="发布状态" name="statuses">
                        <Select
                          mode="multiple"
                          allowClear
                          maxTagCount="responsive"
                          loading={publishState.optionsLoading}
                          options={buildOptionList(publishState.options?.statuses)}
                          placeholder="全部状态"
                        />
                      </Form.Item>
                    </Col>
                    <Col xs={24} md={12} xl={6}>
                      <Form.Item label="趋势粒度" name="granularity">
                        <Select options={GRANULARITY_OPTIONS} />
                      </Form.Item>
                    </Col>
                    <Col xs={24} md={12} xl={10}>
                      <Form.Item label="内容类型" name="templateTypes">
                        <Select
                          mode="multiple"
                          allowClear
                          maxTagCount="responsive"
                          loading={publishState.optionsLoading}
                          options={buildOptionList(publishState.options?.template_types)}
                          placeholder="全部内容类型"
                        />
                      </Form.Item>
                    </Col>
                    <Col xs={24} md={12} xl={8}>
                      <Form.Item label="发布人" name="publisherUserIds">
                        <Select
                          mode="multiple"
                          allowClear
                          maxTagCount="responsive"
                          loading={publishState.optionsLoading}
                          options={buildOptionList(publishState.options?.publisher_users)}
                          placeholder="全部发布人"
                        />
                      </Form.Item>
                    </Col>
                    <Col xs={24} md={12} xl={6}>
                      <Form.Item label="任务 ID" name="taskIds">
                        <Select
                          mode="multiple"
                          allowClear
                          maxTagCount="responsive"
                          loading={publishState.optionsLoading}
                          options={buildOptionList(publishState.options?.tasks)}
                          placeholder="全部任务"
                        />
                      </Form.Item>
                    </Col>
                    <Col xs={24} md={12} xl={8}>
                      <Form.Item label="分组维度" name="groupBy">
                        <Select
                          mode="multiple"
                          allowClear
                          maxCount={3}
                          maxTagCount="responsive"
                          options={PUBLISH_GROUP_OPTIONS}
                          placeholder="最多 3 个维度"
                        />
                      </Form.Item>
                    </Col>
                    <Col xs={24} md={12} xl={6}>
                      <Form.Item label="Top 维度" name="topBy">
                        <Select options={PUBLISH_TOP_OPTIONS} />
                      </Form.Item>
                    </Col>
                  </Row>
                </Form>
              </Card>

              <Alert
                type="info"
                showIcon
                message={`统计周期：${formatDateOnly(publishQueryState.range[0])} 至 ${formatDateOnly(publishQueryState.range[1])}`}
                description={`分组维度：${getGroupLabelText(publishQueryState.groupBy, PUBLISH_DIMENSION_LABELS)}；趋势粒度：${getTopLabelText(publishQueryState.granularity, GRANULARITY_OPTIONS)}；Top 维度：${getTopLabelText(publishQueryState.topBy, PUBLISH_TOP_OPTIONS)}`}
              />

              {publishState.error && (
                <Alert type="error" showIcon message={publishState.error} />
              )}

              <Spin spinning={publishState.loading}>
                <Space direction="vertical" size={16} style={{ width: '100%' }}>
                  <Row gutter={[16, 16]}>
                    <MetricCard title="发布总数" value={publishState.overview?.publish_total || 0} />
                    <MetricCard title="发布成功" value={publishState.overview?.publish_success_total || 0} />
                    <MetricCard title="发布失败" value={publishState.overview?.publish_failed_total || 0} />
                    <MetricCard title="发布中" value={publishState.overview?.publish_publishing_total || 0} />
                    <MetricCard title="覆盖账号数" value={publishState.overview?.distinct_account_total || 0} />
                    <MetricCard title="发布成功率" value={formatPercent(publishState.overview?.publish_success_rate)} text />
                  </Row>

                  <Card title="发布趋势" className="statistics-page__section-card">
                    <Table
                      size="small"
                      pagination={false}
                      columns={[
                        { title: '日期', dataIndex: 'date', key: 'date' },
                        { title: '发布总数', dataIndex: 'publish_total', key: 'publish_total' },
                        { title: '成功', dataIndex: 'publish_success_total', key: 'publish_success_total' },
                        { title: '失败', dataIndex: 'publish_failed_total', key: 'publish_failed_total' },
                        { title: '发布中', dataIndex: 'publish_publishing_total', key: 'publish_publishing_total' },
                        {
                          title: '成功率',
                          dataIndex: 'publish_success_rate',
                          key: 'publish_success_rate',
                          render: (value) => formatPercent(value),
                        },
                      ]}
                      dataSource={publishState.query?.trend || []}
                      rowKey={(record) => `${record.date || 'date'}-${record.publish_total || 0}`}
                      locale={{ emptyText: <Empty description="暂无数据" image={Empty.PRESENTED_IMAGE_SIMPLE} /> }}
                    />
                  </Card>

                  <Card
                    title="分组统计"
                    className="statistics-page__section-card"
                    extra={(
                      <Space wrap>
                        <Text type="secondary">{`共 ${publishTable.total || 0} 条`}</Text>
                        <Button
                          icon={<DownloadOutlined />}
                          onClick={() => void handlePublishExport()}
                          loading={publishState.exporting}
                          disabled={!publishTable.total}
                        >
                          导出 CSV
                        </Button>
                      </Space>
                    )}
                  >
                    <Table
                      size="small"
                      columns={publishTableColumns}
                      dataSource={publishTable.items || []}
                      rowKey={(record, index) => `${record.account_id || record.platform_code || 'row'}-${index}`}
                      pagination={{
                        current: publishTable.page || publishQueryState.page,
                        pageSize: publishTable.page_size || publishQueryState.pageSize,
                        total: publishTable.total || 0,
                        showSizeChanger: true,
                        onChange: (page, pageSize) => {
                          void handlePublishPageChange(page, pageSize);
                        },
                      }}
                      locale={{ emptyText: <Empty description="暂无数据" image={Empty.PRESENTED_IMAGE_SIMPLE} /> }}
                      scroll={{ x: 'max-content' }}
                    />
                  </Card>

                  <Card title={`Top 10 - ${getTopLabelText(publishQueryState.topBy, PUBLISH_TOP_OPTIONS)}`} className="statistics-page__section-card">
                    <Table
                      size="small"
                      pagination={false}
                      columns={[
                        { title: '排名', dataIndex: 'rank', key: 'rank', width: 80 },
                        { title: '维度值', dataIndex: 'rank_label', key: 'rank_label', render: (value) => value || '-' },
                        { title: '发布总数', dataIndex: 'publish_total', key: 'publish_total' },
                        { title: '成功', dataIndex: 'publish_success_total', key: 'publish_success_total' },
                        { title: '失败', dataIndex: 'publish_failed_total', key: 'publish_failed_total' },
                        {
                          title: '成功率',
                          dataIndex: 'publish_success_rate',
                          key: 'publish_success_rate',
                          render: (value) => formatPercent(value),
                        },
                      ]}
                      dataSource={publishState.query?.top?.items || []}
                      rowKey={(record) => `${record.rank_key || 'rank'}-${record.rank || 0}`}
                      locale={{ emptyText: <Empty description="暂无数据" image={Empty.PRESENTED_IMAGE_SIMPLE} /> }}
                      scroll={{ x: 'max-content' }}
                    />
                  </Card>

                  <Card
                    title="失败原因 Top 10"
                    className="statistics-page__section-card"
                    extra={<Text type="secondary">{`失败总数 ${publishState.extra?.failureReasons?.total_failed || 0}`}</Text>}
                  >
                    <Table
                      size="small"
                      pagination={false}
                      columns={[
                        { title: '排名', dataIndex: 'rank', key: 'rank', width: 80 },
                        { title: '错误码', dataIndex: 'error_code', key: 'error_code', render: (value) => value || '-' },
                        { title: '失败次数', dataIndex: 'publish_failed_total', key: 'publish_failed_total' },
                        { title: '示例错误信息', dataIndex: 'sample_error_message', key: 'sample_error_message', render: (value) => value || '-' },
                      ]}
                      dataSource={publishState.extra?.failureReasons?.items || []}
                      rowKey={(record) => `${record.error_code || 'reason'}-${record.rank || 0}`}
                      locale={{ emptyText: <Empty description="暂无数据" image={Empty.PRESENTED_IMAGE_SIMPLE} /> }}
                      scroll={{ x: 'max-content' }}
                    />
                  </Card>
                </Space>
              </Spin>
            </Space>
          </div>
        )}

        {mountedTabs.effect && (
          <div style={{ display: activeTab === 'effect' ? 'block' : 'none' }}>
            <Space direction="vertical" size={16} style={{ width: '100%' }}>
              <Card
                title="筛选条件"
                className="statistics-page__section-card"
                extra={(
                  <Space wrap>
                    <Button icon={<ReloadOutlined />} onClick={() => void loadEffectOptions(true)} loading={effectState.optionsLoading}>
                      重载筛选项
                    </Button>
                    <Button onClick={() => void handleEffectReset()}>
                      重置
                    </Button>
                    <Button type="primary" icon={<SearchOutlined />} onClick={() => void handleEffectSearch()}>
                      查询
                    </Button>
                  </Space>
                )}
              >
                {effectState.optionsError && (
                  <Alert
                    className="statistics-page__alert"
                    type="warning"
                    showIcon
                    message={effectState.optionsError}
                  />
                )}
                <Form
                  form={effectForm}
                  layout="vertical"
                  initialValues={buildEffectFormValues(effectQueryState)}
                >
                  <Row gutter={[16, 8]}>
                    <Col xs={24} md={12} xl={6}>
                      <Form.Item label="统计模式" name="mode">
                        <Select options={EFFECT_MODE_OPTIONS} />
                      </Form.Item>
                    </Col>
                    <Col xs={24} md={12} xl={8}>
                      <Form.Item label="统计时间" name="range">
                        <RangePicker style={{ width: '100%' }} allowClear={false} />
                      </Form.Item>
                    </Col>
                    <Col xs={24} md={12} xl={6}>
                      <Form.Item label="时间字段" name="timeField">
                        <Select options={getEffectTimeFieldOptions(effectMode)} />
                      </Form.Item>
                    </Col>
                    <Col xs={24} md={12} xl={10}>
                      <Form.Item label="平台" name="platformCodes">
                        <Select
                          mode="multiple"
                          allowClear
                          maxTagCount="responsive"
                          loading={effectState.optionsLoading}
                          options={buildOptionList(effectState.options?.platform_codes)}
                          placeholder="全部平台"
                        />
                      </Form.Item>
                    </Col>
                    <Col xs={24} md={12} xl={10}>
                      <Form.Item label="账号" name="accountIds">
                        <Select
                          mode="multiple"
                          allowClear
                          maxTagCount="responsive"
                          loading={effectState.optionsLoading}
                          options={buildAccountOptionList(effectState.options?.accounts)}
                          placeholder="全部账号"
                        />
                      </Form.Item>
                    </Col>
                    <Col xs={24} md={12} xl={8}>
                      <Form.Item label="内容类型" name="templateTypes">
                        <Select
                          mode="multiple"
                          allowClear
                          maxTagCount="responsive"
                          loading={effectState.optionsLoading}
                          options={buildOptionList(effectState.options?.template_types)}
                          placeholder="全部内容类型"
                        />
                      </Form.Item>
                    </Col>
                    <Col xs={24} md={12} xl={8}>
                      <Form.Item label="内容来源" name="contentSources">
                        <Select
                          mode="multiple"
                          allowClear
                          maxTagCount="responsive"
                          loading={effectState.optionsLoading}
                          options={buildOptionList(effectState.options?.content_sources)}
                          placeholder="全部内容来源"
                        />
                      </Form.Item>
                    </Col>
                    <Col xs={24} md={12} xl={8}>
                      <Form.Item label="生产器" name="producers">
                        <Select
                          mode="multiple"
                          allowClear
                          maxTagCount="responsive"
                          loading={effectState.optionsLoading}
                          options={buildOptionList(effectState.options?.producers)}
                          placeholder="全部生产器"
                        />
                      </Form.Item>
                    </Col>
                    <Col xs={24} md={12} xl={8}>
                      <Form.Item label="同步状态" name="effectSyncStatuses">
                        <Select
                          mode="multiple"
                          allowClear
                          maxTagCount="responsive"
                          loading={effectState.optionsLoading}
                          options={buildOptionList(effectState.options?.effect_sync_statuses)}
                          placeholder="全部同步状态"
                        />
                      </Form.Item>
                    </Col>
                    <Col xs={24} md={12} xl={6}>
                      <Form.Item label="趋势粒度" name="granularity">
                        <Select options={GRANULARITY_OPTIONS} />
                      </Form.Item>
                    </Col>
                    <Col xs={24} md={12} xl={10}>
                      <Form.Item label="任务 ID" name="taskIds">
                        <Select
                          mode="multiple"
                          allowClear
                          maxTagCount="responsive"
                          loading={effectState.optionsLoading}
                          options={buildOptionList(effectState.options?.tasks)}
                          placeholder="全部任务"
                        />
                      </Form.Item>
                    </Col>
                    <Col xs={24} md={12} xl={8}>
                      <Form.Item label="分组维度" name="groupBy">
                        <Select
                          mode="multiple"
                          allowClear
                          maxCount={3}
                          maxTagCount="responsive"
                          options={EFFECT_GROUP_OPTIONS}
                          placeholder="最多 3 个维度"
                        />
                      </Form.Item>
                    </Col>
                    <Col xs={24} md={12} xl={6}>
                      <Form.Item label="Top 指标" name="topMetric">
                        <Select options={EFFECT_METRIC_OPTIONS} />
                      </Form.Item>
                    </Col>
                  </Row>
                </Form>
              </Card>

              <Alert
                type="info"
                showIcon
                message={`统计周期：${formatDateOnly(effectQueryState.range[0])} 至 ${formatDateOnly(effectQueryState.range[1])}`}
                description={`统计模式：${getTopLabelText(effectQueryState.mode, EFFECT_MODE_OPTIONS)}；分组维度：${getGroupLabelText(effectQueryState.groupBy, EFFECT_DIMENSION_LABELS)}；趋势粒度：${getTopLabelText(effectQueryState.granularity, GRANULARITY_OPTIONS)}；Top 指标：${getTopLabelText(effectQueryState.topMetric, EFFECT_METRIC_OPTIONS)}；最近同步：${formatDateTimeText(effectState.overview?.latest_sync_time)}`}
              />

              {effectState.error && (
                <Alert type="error" showIcon message={effectState.error} />
              )}

              <Spin spinning={effectState.loading}>
                <Space direction="vertical" size={16} style={{ width: '100%' }}>
                  <Row gutter={[16, 16]}>
                    <MetricCard title="作品数" value={effectState.overview?.publish_total || 0} />
                    <MetricCard title="已同步作品" value={effectState.overview?.synced_publish_total || 0} />
                    <MetricCard title="同步覆盖率" value={formatPercent(effectState.overview?.sync_coverage_rate)} text />
                    <MetricCard title="覆盖账号数" value={effectState.overview?.distinct_account_total || 0} />
                    <MetricCard title={getEffectMetricLabel('view_count', effectQueryState.mode)} value={effectState.overview?.view_count || 0} />
                    <MetricCard title={getEffectMetricLabel('like_count', effectQueryState.mode)} value={effectState.overview?.like_count || 0} />
                    <MetricCard title={getEffectMetricLabel('comment_count', effectQueryState.mode)} value={effectState.overview?.comment_count || 0} />
                    <MetricCard title={getEffectMetricLabel('share_count', effectQueryState.mode)} value={effectState.overview?.share_count || 0} />
                    <MetricCard title={getEffectMetricLabel('favorite_count', effectQueryState.mode)} value={effectState.overview?.favorite_count || 0} />
                  </Row>

                  <Card title="效果趋势" className="statistics-page__section-card">
                    <Table
                      size="small"
                      pagination={false}
                      columns={[
                        { title: '日期', dataIndex: 'date', key: 'date' },
                        { title: '作品数', dataIndex: 'publish_total', key: 'publish_total' },
                        ...EFFECT_METRIC_OPTIONS.map((option) => ({
                          title: getEffectMetricLabel(option.value, effectQueryState.mode),
                          dataIndex: option.value,
                          key: option.value,
                        })),
                      ]}
                      dataSource={effectState.query?.trend || []}
                      rowKey={(record) => `${record.date || 'date'}-${record.publish_total || 0}`}
                      locale={{ emptyText: <Empty description="暂无数据" image={Empty.PRESENTED_IMAGE_SIMPLE} /> }}
                      scroll={{ x: 'max-content' }}
                    />
                  </Card>

                  <Card
                    title="分组统计"
                    className="statistics-page__section-card"
                    extra={(
                      <Space wrap>
                        <Text type="secondary">{`共 ${effectTable.total || 0} 条`}</Text>
                        <Button
                          icon={<DownloadOutlined />}
                          onClick={() => void handleEffectExport()}
                          loading={effectState.exporting}
                          disabled={!effectTable.total}
                        >
                          导出 CSV
                        </Button>
                      </Space>
                    )}
                  >
                    <Table
                      size="small"
                      columns={effectTableColumns}
                      dataSource={effectTable.items || []}
                      rowKey={(record, index) => `${record.account_id || record.platform_code || 'effect'}-${index}`}
                      pagination={{
                        current: effectTable.page || effectQueryState.page,
                        pageSize: effectTable.page_size || effectQueryState.pageSize,
                        total: effectTable.total || 0,
                        showSizeChanger: true,
                        onChange: (page, pageSize) => {
                          void handleEffectPageChange(page, pageSize);
                        },
                      }}
                      locale={{ emptyText: <Empty description="暂无数据" image={Empty.PRESENTED_IMAGE_SIMPLE} /> }}
                      scroll={{ x: 'max-content' }}
                    />
                  </Card>

                  <Card title={`Top 10 内容 - ${getTopLabelText(effectQueryState.topMetric, EFFECT_METRIC_OPTIONS)}`} className="statistics-page__section-card">
                    <Table
                      size="small"
                      pagination={false}
                      columns={effectTopContentColumns}
                      dataSource={effectState.query?.top_content?.items || []}
                      rowKey={(record) => `${record.publish_id || 'effect-top'}-${record.rank || 0}`}
                      locale={{ emptyText: <Empty description="暂无数据" image={Empty.PRESENTED_IMAGE_SIMPLE} /> }}
                      scroll={{ x: 'max-content' }}
                    />
                  </Card>
                </Space>
              </Spin>
            </Space>
          </div>
        )}
      </Card>
    </div>
  );
};

const MetricCard = ({ title, value, text = false }) => (
  <Col xs={24} sm={12} lg={8} xl={8} xxl={4}>
    <Card className="statistics-page__metric-card">
      <Statistic
        title={title}
        value={value}
        valueStyle={text ? { fontSize: 24 } : undefined}
      />
    </Card>
  </Col>
);

export default Statistics;
